import { useEffect, useMemo, useState } from "react";
import { BaseRepository } from "./base-repository";
import { sql } from "@/domains/database/utils";

export type SmartContextRow = {
  id: string;
  associated_space_account_id: string;
  payload_json: unknown;
  created_at: string | null;
  updated_at: string | null;
};

export class SmartContextRepository extends BaseRepository {
  async upsertContext(params: SmartContextRow): Promise<void> {
    const {
      id,
      associated_space_account_id,
      payload_json,
      created_at = new Date().toISOString(),
      updated_at = new Date().toISOString(),
    } = params;

    await this.db.query(
      sql`
        INSERT INTO smart_contexts (
          id,
          associated_space_account_id,
          payload_json,
          created_at,
          updated_at
        ) VALUES ($1, $2, $3, $4, $5)
        ON CONFLICT (id) DO UPDATE SET
          associated_space_account_id = EXCLUDED.associated_space_account_id,
          payload_json = EXCLUDED.payload_json,
          updated_at = EXCLUDED.updated_at
      `,
      [id, associated_space_account_id, JSON.stringify(payload_json), created_at, updated_at]
    );
  }

  useById({ contextId }: { contextId: string | null }): {
    context: SmartContextRow | null;
    isLoading: boolean;
    error: string | null;
  } {
    const [context, setContext] = useState<SmartContextRow | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const query = useMemo(
      () =>
        sql`
          SELECT *
          FROM smart_contexts
          WHERE id = $1
          LIMIT 1
        `,
      []
    );

    const isReady = this.connectionManager.isReady();

    useEffect(() => {
      if (!contextId) {
        setContext(null);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      if (!isReady) {
        return;
      }

      const controller = new AbortController();
      const db = this.getDbIfReady();
      if (!db) {
        return () => {
          controller.abort();
        };
      }
      let cleanup: (() => void) | undefined;

      db.live
        .query<SmartContextRow>({ query, params: [contextId], signal: controller.signal })
        .then(({ initialResults, subscribe, unsubscribe }) => {
          const apply = (rows: SmartContextRow[]) => {
            setContext(rows[0] ?? null);
            setIsLoading(false);
          };

          apply(initialResults.rows as SmartContextRow[]);

          const onUpdate = (results: { rows: SmartContextRow[] }) => apply(results.rows);
          subscribe(onUpdate);
          cleanup = () => void unsubscribe(onUpdate);
        })
        .catch(err => {
          if (controller.signal.aborted) return;
          setError(err?.message ?? "Unknown error");
          setIsLoading(false);
        });

      return () => {
        controller.abort();
        if (cleanup) cleanup();
      };
    }, [query, contextId, isReady]);

    return { context, isLoading, error };
  }
}
